<template>
  <base-layout :withHeaderStructure="true">
    <template v-slot:header>
      <h1 class="text-2xl font-bold mt-4 mb-2">Two-Factor Authentication (2FA)</h1>
    </template>
    <template v-slot:main>
      <div class="max-w-xl mx-auto mt-8">
        <div class="mb-6">
          <p>
            Two-Factor Authentication (2FA) adds an extra layer of security to your account by requiring a code from your phone in addition to your password.
          </p>
        </div>
        <div class="mb-8">
          <div v-if="user && user.enable2fa">
            <p class="mb-4 text-green-700 font-semibold">2FA is currently <span class="font-bold">ENABLED</span> on your account.</p>
            <button class="btn btn-primary" @click="disable2fa">Disable 2FA</button>
          </div>
          <div v-else>
            <p class="mb-4 text-yellow-700 font-semibold">2FA is currently <span class="font-bold">DISABLED</span> on your account.</p>
            <button class="btn btn-primary" @click="enable2fa">Enable 2FA</button>
          </div>
        </div>
      </div>

      <!-- 2FA Modal -->
      <modal v-show="is2faModalVisible" @cancel="close2faModal"
             :header="user && user.enable2fa ? 'Disable 2FA' : 'Enable 2FA'"
             affirmativeButtonLabel="Verify Code"
             :showAffirmativeButton="showVerification"
             @submit="verify2faCode">
        <template v-slot:body>
          <div v-if="show2faDescription">
            <p class="mb-4 text-sm text-gray-600">
              To enable 2-Factor Authentication (2FA), you will need to have a 2FA app (Google Authenticator, Authy, or Microsoft Authenticator) installed on your phone. <br><br>
              <strong class="text-red-700">Important: do not scan the QR code with your phone's camera app, only use the authenticator app to scan it.</strong><br><br>
              <strong>Did you install the authenticator app?</strong> Use it to scan this QR code, or enter the key below it manually:
            </p>
            <div class="flex justify-center">
              <img v-if="qrCode" :src="qrCode" alt="QR Code for 2FA" />
              <div v-else>Loading QR code...</div>
            </div>
            <!-- Add this for debugging -->
            <div v-if="secret" class="mt-2 text-xs text-gray-500 text-center">
              {{ secret }}
            </div>
            <p class="mt-4 mb-4 text-sm text-gray-600 text-center">
              <a href="javascript:void(0)" @click="proceedToVerification" class="text-red-700 hover:underline cursor-pointer">
                I've scanned the QR code - Verify my setup
              </a>
            </p>
            <p class="mt-4 mb-4 text-sm text-gray-600 text-center">
              <a href="javascript:void(0)" @click="show2faDescription = false" class="text-red-700 hover:underline cursor-pointer">
                What is 2-Factor Authentication (2FA)?
              </a>
            </p>
          </div>
          <div v-else-if="showVerification">
            <p class="mb-4 text-sm text-gray-600">
              <span v-if="user && user.enable2fa">
                <strong>WARNING:<br>Disabling 2-Factor Authentication (2FA) will restrict your account for security purposes.</strong><br>You will not be able to access your account until you re-enable 2FA.<br><br>
                To disable 2FA, please enter the 6-digit code shown in your authenticator app:
              </span>
              <span v-else>
                To complete the 2FA setup, please enter the 6-digit code shown in your authenticator app:
              </span>
            </p>
            <div class="mt-1 relative rounded-md shadow-sm">
              <input
                ref="verificationInput"
                v-model="verificationCode"
                type="text"
                maxlength="6"
                pattern="[0-9]*"
                inputmode="numeric"
                class="form-input block w-full sm:text-sm sm:leading-5"
                placeholder="Enter 6-digit code"
              />
            </div>
            <p v-if="verificationError" class="mt-2 text-sm text-red-600">
              {{ verificationError }}
            </p>
          </div>
          <div v-else>
            <p class="mb-4 text-sm text-gray-600"><br><strong>What is 2-Factor Authentication (2FA)?</strong><br><br>2-Factor Authentication (2FA) is a security feature that requires two forms of identification to access your account. This adds an extra layer of security to your account by requiring not only your password but also a one-time code generated by your authenticator app.<br>
              This makes it much more difficult for unauthorized users to access your account.<br><br>Enabling 2FA is required, without it <strong>your account will be restricted</strong> and you will not be able to access your account until you re-enable 2FA.</p>
            <p class="mt-4 mb-4 text-sm text-gray-600 text-center">
              <a href="javascript:void(0)" @click="show2faDescription = true" class="text-red-700 hover:underline cursor-pointer">
                Scan my QR Code
              </a>
            </p>
          </div>
        </template>
      </modal>
    </template>
  </base-layout>
</template>

<script>
import { mapState } from 'vuex'
import Modal from '@/components/modal.vue'
import BaseLayout from '@/components/BaseLayout.vue'
import eventBus from '../utils/eventBus'
import store from '../store'

export default {
  name: 'TwoFactorAuth',
  components: {
    Modal,
    BaseLayout,
  },
  beforeRouteEnter(to, from, next) {
    store.dispatch('getProfile').then(resp => {
      if(resp.status !== 200) return next("/")
      next()
    })
  },
  data() {
    return {
      is2faModalVisible: false,
      qrCode: "",
      qrCodeContent: "",
      secret: "", // Add this to store the extracted secret
      show2faDescription: true,
      showVerification: false,
      verificationCode: '',
      verificationError: '',
    }
  },
  computed: {
    ...mapState(['user']),
  },
  methods: {
    extractSecretFromQrContent(qrContent) {
      try {
        const url = new URL(qrContent);
        return url.searchParams.get('secret');
      } catch (error) {
        console.error('Failed to parse QR content:', error);
        return null;
      }
    },
    enable2fa() {
      this.$http.post('/api/companies/generate2faSecret')
        .then(response => {
          if (response.data.status === 200) {
            const secret = response.data.secret;
            const otpauthUrl = `otpauth://totp/TheBrokerSite:${this.user.email}?secret=${secret}&issuer=TheBrokerSite`;
            this.qrCode = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpauthUrl)}`;
            this.qrCodeContent = otpauthUrl;
            this.secret = this.extractSecretFromQrContent(otpauthUrl); // Extract and store the secret
            this.is2faModalVisible = true;
            this.show2faDescription = true;
            this.showVerification = false;
            this.verificationCode = '';
            this.verificationError = '';
          } else {
            throw new Error('Failed to generate 2FA secret');
          }
        })
        .catch(error => {
          eventBus.$emit("createSnackbar", {
            type: "error",
            title: "Could not start 2FA setup",
            body: "Please try again. If this keeps happening, contact support.",
            timeoutInMS: 5000,
          });
        });
    },
    disable2fa() {
      this.is2faModalVisible = true;
      this.show2faDescription = false;
      this.showVerification = true;
      this.verificationCode = '';
      this.verificationError = '';
    },
    close2faModal() {
      this.is2faModalVisible = false;
      setTimeout(() => {
        this.show2faDescription = true;
        this.showVerification = false;
        this.verificationCode = '';
        this.verificationError = '';
      }, 300);
    },
    proceedToVerification() {
      this.showVerification = true;
      this.show2faDescription = false;
    },
    async verify2faCode() {
      this.verificationError = '';
      if (!this.verificationCode || this.verificationCode.length !== 6) {
        this.verificationError = 'Please enter a valid 6-digit code';
        return;
      }
      try {
        if (this.user && this.user.enable2fa) {
          // Disabling 2FA
          const response = await this.$http.post('/api/companies/verify2faForDisable', {
            code: this.verificationCode
          });
          if (response.data.status === 200) {
            const disableResponse = await this.$http.post('/api/companies/disable2fa', {});
            if (disableResponse.data.status === 200) {
              this.$store.commit('updateUser', { ...this.user, enable2fa: false });
              this.close2faModal();
              eventBus.$emit("createSnackbar", {
                type: "success",
                title: "2FA Successfully Disabled",
                body: "Two-factor authentication has been disabled for your account",
              });
            }
          } else {
            this.verificationError = 'Invalid code. Please try again.';
          }
        } else {
          // Enabling 2FA
          const response = await this.$http.post('/api/companies/verify2fa', {
            code: this.verificationCode
          });
          if (response.data.status === 200) {
            this.$store.commit('updateUser', { ...this.user, enable2fa: true });
            this.close2faModal();
            eventBus.$emit("createSnackbar", {
              type: "success",
              title: "2FA Successfully Enabled",
              body: "Two-factor authentication has been enabled for your account",
            });
          } else {
            this.verificationError = 'Invalid code. Please try again.';
          }
        }
      } catch (error) {
        this.verificationError = 'Failed to verify code. Please try again.';
      }
    },
  }
}
</script>
